import React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, openModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { setFieldsForPauseLiveSubscriptionModal, setFieldsForMultiMonthPauseModal } from "../../../state/ArbitrarySlice/ArbitratySlice.ts";
import { SubscriptionType } from "../../../enums.ts";
import { CircularProgress, IconButton, TextField, Checkbox, Tooltip } from "@mui/material";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { toast } from "react-toastify";
import { SECRETS_ENV } from "../../../Config/firebase.ts";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { Timestamp } from 'firebase/firestore';
import { ActionType } from '../../../enums.ts';
import { logAction } from '../utilityFunctions.ts';
import InputAdornment from '@mui/material/InputAdornment';

interface Item {
    name: string;
    price: string;
    quantity: string;
}

// Professional currency formatting function
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount).replace('₹', '₹ ');
};


export default function OrderSummary() {

    const Dispatch = useAppDispatch();
    const ServiceProviderID = useAppSelector(selectEffectiveUID)

    const open = useAppSelector((state: RootState) => state.ModalSlice.OrderSummaryModalOpen);

    // Subs and LiveSubs Docs

    const { orderId } = useAppSelector((state: RootState) => state.ArbitrarySlice.ForOrderSummaryModal);

    const AllOrders = useAppSelector((state: RootState) => state.OrderStatsSlice.data)
    const LiveSubs = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data)
    const AllSubscriptions = useAppSelector((state: RootState) => state.subscriptionTableSlice.data)

    const SelectedOrderDoc = AllOrders ? AllOrders.filter(doc => doc.id === orderId)[0] : null;
    const selectedLiveSub = LiveSubs?.find(sub => sub.id === SelectedOrderDoc?.livesubscriptionId) ?? null;
    const selectedSubscription = AllSubscriptions?.find(sub => sub.id === SelectedOrderDoc?.subscriptionId) ?? null;
    const isMonthlyOrMultiMonth = selectedLiveSub?.subscriptionType === SubscriptionType.Monthly || selectedLiveSub?.subscriptionType === SubscriptionType.MultiMonth;

    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}

    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)

    // Component States


    const [orderAmount, setOrderAmount] = React.useState<string>("0");
    const [prevPrice, setPrevPrice] = React.useState<number | null>(null);
    const [orderType, setOrderType] = useState<string>("cash")
    const [cancelOrder, setCancelOrder] = useState<boolean>(false);
    const [pauseStudent, setPauseStudent] = useState<boolean>(true);
    const [modalError, setModalError] = React.useState({
        OrderAmount: false,
    });
    const [, setIsError] = useState<boolean>(false);
    const [items, setItems] = useState<Item[]>([]);
    const [bQuickCollect, setBQuickCollect] = useState<boolean>(false);
    const [orderNote, setOrderNote] = useState<string>("");

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderID}/Orders`)

    // Error state for all items (using array indices)
    const [itemErrors, setItemErrors] = useState<{ name: boolean, price: boolean, quantity: boolean }[]>([]);

    useEffect(() => {
        if (SelectedOrderDoc) {
            const isQuickCollect = Boolean(SelectedOrderDoc.bQuickCollect);
            
            // Handle items first to calculate their total
            let itemsArray: Item[] = [];
            if (SelectedOrderDoc.items && SelectedOrderDoc.items.length > 0) {
                itemsArray = SelectedOrderDoc.items.map((item: any) => ({
                    name: item.name ?? '',
                    price: item.price !== undefined && item.price !== null ? String(item.price) : '',
                    quantity: item.quantity !== undefined && item.quantity !== null ? String(item.quantity) : '1',
                }));
                setItems(itemsArray);
                setItemErrors(itemsArray.map(() => ({ name: false, price: false, quantity: false })));
            } else if (isQuickCollect) {
                // Quick Collect orders must have at least one item
                itemsArray = [{ name: '', price: '', quantity: '1' }];
                setItems(itemsArray);
                setItemErrors([{ name: false, price: false, quantity: false }]);
            } else {
                setItems([]);
                setItemErrors([]);
            }
            
            // Calculate items total from current items
            const itemsTotal = itemsArray.reduce((total, item) => {
                const priceNum = parseFloat(item.price) || 0;
                const quantityNum = parseFloat(item.quantity) || 0;
                return total + (priceNum * quantityNum);
            }, 0);
            
            // orderAmount = chargeable - (sum of item prices)
            const baseOrderAmount = SelectedOrderDoc.chargeable - itemsTotal;
            
            setOrderAmount(baseOrderAmount.toString())
            setOrderType(SelectedOrderDoc.orderType)
            setCancelOrder(false)
            setPrevPrice(SelectedOrderDoc.chargeable)
            setBQuickCollect(isQuickCollect)
            setOrderNote(SelectedOrderDoc.orderNote || "")
        } 
    }, [SelectedOrderDoc,open])


    // Handle onChange

    const handleChangeOrderAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs

        if (!value) {
            setOrderAmount("")
            return;
        }

        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {

            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }

        setOrderAmount(value)
    }

    // Calculate total for an item
    const calculateItemTotal = (price: string, quantity: string) => {
        const priceNum = parseFloat(price) || 0;
        const quantityNum = parseFloat(quantity) || 0;
        return priceNum * quantityNum;
    };

    // Calculate grand total from items
    const calculateGrandTotal = () => {
        return items.reduce((total, item) => {
            return total + calculateItemTotal(item.price, item.quantity);
        }, 0);
    };

    // Calculate overall total for submission: orderAmount + (sum of item prices)
    const calculateOverallTotal = () => {
        const baseAmount = parseFloat(orderAmount) || 0;
        const itemsTotal = calculateGrandTotal();
        return baseAmount + itemsTotal;
    };

    // Check if current item is complete
    const isItemComplete = (item: Item) => {
        return item.name.trim() !== '' && item.price.trim() !== '' && item.quantity.trim() !== '';
    };

    // Check if can add new item
    const canAddItem = () => {
        if (items.length === 0) return true;
        const lastItem = items[items.length - 1];
        return isItemComplete(lastItem);
    };

    // Update item field
    const updateItem = (index: number, field: keyof Item, value: string) => {
        setItems(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    // Add new item
    const addItem = () => {
        if (canAddItem()) {
            setItems(prev => [...prev, { name: '', price: '', quantity: '1' }]);
            setItemErrors(prev => [...prev, { name: false, price: false, quantity: false }]);
        }
    };

    // Remove item
    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(prev => prev.filter((_, i) => i !== index));
            setItemErrors(prev => prev.filter((_, i) => i !== index));
        } else if (items.length === 1 && !bQuickCollect) {
            // Only allow removing the last item if it's not a Quick Collect order
            setItems([]);
            setItemErrors([]);
        }
        // For Quick Collect orders, we don't allow removing the last item
    };

    // Handle price input (only numbers and decimal)
    const handlePriceChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value)) {
            updateItem(index, 'price', value);
        }
    };

    // Handle quantity input (positive numbers including decimals, not zero)
    const handleQuantityChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value) && value !== '0' && value !== '0.') {
            updateItem(index, 'quantity', value);
        }
    };

    // Check errors for items
    const checkItemErrors = () => {
        let hasErrors = false;
        const newItemErrors: { name: boolean, price: boolean, quantity: boolean }[] = [];

        items.forEach((item, index) => {
            const errors = {
                name: item.name.trim() === '',
                price: parseFloat(item.price) < 6,
                quantity: parseFloat(item.quantity) < 1
            };

            if (errors.name || errors.price || errors.quantity) {
                hasErrors = true;
            }

            newItemErrors[index] = errors;
        });

        setItemErrors(newItemErrors);
        return hasErrors;
    };


    // Error check
    function CheckErrors(): boolean {
        setIsError(false)
        let localerror: boolean = false


        if (!bQuickCollect && (!orderAmount || orderAmount.trim() === "" || orderAmount.trim() === "0")) {
            localerror = true
            setIsError(true)
            setModalError((prev) => {
                return { ...prev, OrderAmount: true }
            })
        } else {

            setModalError((prev) => {
                return { ...prev, OrderAmount: false }
            })
        }

        // Check item errors only if order is editable
        if (SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") {
            const itemsHaveErrors = checkItemErrors();
            if (itemsHaveErrors) {
                localerror = true;
                setIsError(true);
            }

            // For Quick Collect orders, ensure at least one item exists
            if (bQuickCollect && items.length === 0) {
                localerror = true;
                setIsError(true);
            }
        }

        return localerror

    }

    // OnSubmit function

    const { axiosPostRequest } = useBackend("")

    async function handleAPICall() {
        Dispatch(toggleLoading(true))

        if (cancelOrder) {
            // Call the cancel order API
            const reqBody = {
                orderMonth: SelectedOrderDoc?.cycleName,
                serviceProviderId: ServiceProviderID,
                livesubscriptionId: SelectedOrderDoc?.livesubscriptionId,
                subscriptionId: SelectedOrderDoc?.subscriptionId,
                orderId: SelectedOrderDoc?.id
            }
            try {
                const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/writeOffExistingOrder`, reqBody)
                if (response.message !== "Request successful") {
                    Dispatch(toggleLoading(false))
                    return "error"
                }

                update(SelectedOrderDoc?.id, {
                    orderNote: orderNote ? orderNote.trim() : ""
                })

                logAction({
                    action: ActionType.cancelOrder,
                    timestamp: Timestamp.now(),
                    platform: "web",
                    performedBy: {
                        spId: spId || "",
                        name: spName || ""
                    },
                    livesubscriptionId: SelectedOrderDoc?.livesubscriptionId,
                    context: {
                        orderId: SelectedOrderDoc?.id,
                        prevPrice: prevPrice,
                        livesubscriptionId: SelectedOrderDoc?.livesubscriptionId,
                    },

                }, ServiceProviderID)


            } catch (error) {
                Dispatch(toggleLoading(false))
                return "error"
            } finally {
                Dispatch(toggleLoading(false))
            }
            return "success"
        } else {
            // Regular update order API
            if (!SelectedOrderDoc) {
                Dispatch(toggleLoading(false))
                return "error"
            }
            const updateBody = {
                providerId: ServiceProviderID,
                orderId: SelectedOrderDoc.id,
                amount: Number(calculateOverallTotal()),
                orderType: orderType,
                items: items.map(item => ({
                    name: item.name,
                    price: Number(item.price),
                    quantity: Number(item.quantity)
                }))
            }
            try {
                const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/updateOrder`, updateBody)
                if (response.message !== "Request successful") {
                    Dispatch(toggleLoading(false))
                    return "error"
                }

                logAction({
                    action: ActionType.editOrder,
                    timestamp: Timestamp.now(),
                    platform: "web",
                    performedBy: { 
                        spId: spId || "",
                        name: spName || ""
                    },
                    livesubscriptionId: SelectedOrderDoc?.livesubscriptionId,
                    context: {
                        orderId: SelectedOrderDoc?.id,
                        prevPrice: prevPrice,
                        livesubscriptionId: SelectedOrderDoc?.livesubscriptionId,
                        newPrice: Number(calculateOverallTotal()),
                        baseAmount: Number(orderAmount),
                        itemsTotal: calculateGrandTotal(),
                        bQuickCollect: bQuickCollect,
                    },

                }, ServiceProviderID)
            } catch (error) {
                Dispatch(toggleLoading(false))
                return "error"
            } finally {
                Dispatch(toggleLoading(false))
            }
            return "success"
        }
    }


    async function onSubmit(e: any) {
        e.preventDefault()
        const Localerrors = CheckErrors()

        if (Localerrors) {
            return
        }
        const apiSuccess = await handleAPICall()
        if (apiSuccess === "error") {
            toast.error("Error updating Order details")
        }

        const shouldOpenPause = apiSuccess === "success" && cancelOrder && pauseStudent && isMonthlyOrMultiMonth;
        const capturedLiveSubId = selectedLiveSub?.id ?? "";
        const capturedLiveSubCycle = selectedLiveSub?.cycle ?? "";
        const capturedAlign2monthly = selectedSubscription?.align2monthlyBoundary ?? false;
        const capturedSubId = SelectedOrderDoc?.subscriptionId ?? "";

        handleClose()

        if (shouldOpenPause) {
            if (capturedLiveSubCycle === "MultiMonth") {
                Dispatch(setFieldsForMultiMonthPauseModal({
                    LiveSubscriptionId: capturedLiveSubId,
                    align2monthlyBoundary: capturedAlign2monthly
                }))
                Dispatch(openModal("MultiMonthPauseModalOpen"))
            } else {
                Dispatch(setFieldsForPauseLiveSubscriptionModal({
                    subscriptionId: capturedSubId,
                    LiveSubscriptionId: capturedLiveSubId
                }))
                Dispatch(openModal("PauseLiveSubscriptionModalOpen"))
            }
        }
    }

    // Modal handlers

    function resetStates() {
        setItems([]);
        setItemErrors([]);
        setOrderAmount("1.0");
        setOrderType("cash");
        setCancelOrder(false);
        setPauseStudent(true);
        setPrevPrice(null);
        setBQuickCollect(false);
        setOrderNote("");
    }

    function handleClose() {
        resetStates();
        Dispatch(closeModal("OrderSummaryModalOpen"))
    }

    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
            maxWidth="sm"
            fullWidth={true}
            open={open}

            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingY: "30px", paddingX: "50px" }} className={"bg-white"}>
                <div className={"header pb-[10px] "}>
                    <p className={"text-cf_heading font-[600] "}>{SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed" ? "Edit Order" : "Order Summary"}</p>

                    <div className={"pt-0"}>
                        <p>Name : {SelectedOrderDoc?.studentName}</p>
                        <p>Subscription : {SelectedOrderDoc?.subscriptionName}</p>
                        <p>Cycle : {SelectedOrderDoc?.cycleName}</p>
                    </div>
                </div>

                {SelectedOrderDoc?.orderType === "writeoff" && (
                    <div className={"flex items-center gap-4"}>
                        <p className={"text-red-500 font-semibold"}>This order has been cancelled.</p>
                    </div>
                )}

                <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton></div>

                {/* Only show order details if not cancelled */}
                {SelectedOrderDoc?.orderType !== "writeoff" && (
                    <>
                        {!bQuickCollect && SelectedOrderDoc && <div className={"flex items-center gap-4"}>
                            <div className={"w-full mt-4"}>
                                {(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") ? <TextField
                                    onChange={handleChangeOrderAmount}
                                    value={orderAmount}
                                    size={"small"}
                                    required={true}
                                    id="OrderAmount"
                                    label="Order Amount"
                                    error={modalError.OrderAmount}
                                    helperText={modalError.OrderAmount ? "Settlement amount cannot be zero" : ""}
                                    variant="outlined"
                                    fullWidth={true}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px'// Default border color
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey', // Border color on hover
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue', // Border color when focused
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px', // Setting font size
                                            },
                                        },
                                        fontWeight: 400,

                                    }}
                                /> : (
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                        <p className="text-sm text-gray-600 mb-1">Base Order Amount</p>
                                        <p className={"text-lg font-semibold"}>Rs.{parseFloat(orderAmount || "0").toFixed(2)}</p>
                                        {SelectedOrderDoc?.paymentstatus === "Paid" && (
                                            <p className={"text-sm text-gray-600 mt-2"}>Platform Fees: Rs.{SelectedOrderDoc?.platformFees || 0}</p>
                                        )}
                                    </div>
                                )}
                            </div>

                        </div>}

                        {/* Items Section */}
                        <div className="space-y-4 mt-6">
                            <div className="flex justify-between items-center">
                               { (SelectedOrderDoc?.paymentstatus!=="Paid" || items.length>0) && <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {bQuickCollect ? "Items" : "Additional Items"}
                                    </h3>
                                    {bQuickCollect && (
                                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-md font-medium">
                                            Quick Collect
                                        </span>
                                    )}
                                    {items.length === 0 && (SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") && (
                                        <button
                                            onClick={addItem}
                                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                        >
                                            + Add Items
                                        </button>
                                    )}
                                </div>}
                                {items.length > 0 && (
                                    <div className="text-right">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Items Total</p>
                                        <p className="text-lg font-semibold text-gray-900 tabular-nums">{formatCurrency(calculateGrandTotal())}</p>
                                    </div>
                                )}
                            </div>

                            {items.length > 0 && (
                                <>
                                    {items.map((item, index) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-medium text-gray-700 text-sm">Item {index + 1}</h4>
                                                <div className="flex items-center gap-4">
                                                    {calculateItemTotal(item.price, item.quantity) > 0 && (
                                                        <div className="text-right">
                                                            <div className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg border border-blue-200">
                                                                <span className="text-sm font-semibold tabular-nums">
                                                                    {formatCurrency(calculateItemTotal(item.price, item.quantity))}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") &&
                                                        !(bQuickCollect && items.length === 1) && (
                                                            <button
                                                                onClick={() => removeItem(index)}
                                                                className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <TextField
                                                    value={item.name}
                                                    size='small'
                                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                    label="Item Name"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="Enter item name"
                                                    disabled={!(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed")}
                                                    error={itemErrors[index]?.name || false}
                                                    helperText={itemErrors[index]?.name ? "Item name is required" : ""}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />

                                                <TextField
                                                    value={item.price}
                                                    size='small'
                                                    onChange={(e) => handlePriceChange(index, e.target.value)}
                                                    label="Price"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="0.00"
                                                    disabled={!(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed")}
                                                    error={itemErrors[index]?.price || false}
                                                    helperText={itemErrors[index]?.price ? "Minimum ₹6 required" : ""}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />

                                                <TextField
                                                    value={item.quantity}
                                                    size='small'
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    label="Quantity"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="1.0"
                                                    disabled={!(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed")}
                                                    error={itemErrors[index]?.quantity || false}
                                                    helperText={itemErrors[index]?.quantity ? "Minimum 1 required" : ""}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") && (
                                        <button
                                            onClick={addItem}
                                            disabled={!canAddItem()}
                                            className={`w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-all ${canAddItem()
                                                ? 'border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                                                : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                        >
                                            + Add Item
                                        </button>
                                    )}
                                </>
                            )}

                           

                            {/* Order Note Section - Show for paid orders */}
                            {SelectedOrderDoc?.paymentstatus === "Paid" && (
                                <div className="mt-4">
                                    <TextField
                                        value={orderNote}
                                        onChange={(e) => setOrderNote(e.target.value)}
                                        size="small"
                                        label="Order Notes"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Add notes about this order..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey',
                                                    borderRadius: '10px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            },
                                            fontWeight: 400,
                                        }}
                                    />
                                </div>
                            )}

                            {/* Overall Total Section - Show for all orders */}
                            {calculateOverallTotal() > 0 && (
                                <div className={`bg-gradient-to-r ${SelectedOrderDoc?.paymentstatus === "Paid" ? 'from-green-50 to-emerald-50 border-green-100' : 'from-blue-50 to-indigo-50 border-blue-100'} border rounded-lg p-4 mt-4`}>
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-sm text-gray-800 font-semibold tracking-wide">
                                                {SelectedOrderDoc?.paymentstatus === "Paid" ? "Total Paid Amount" : "Total Chargeable Amount"}
                                            </p>
                                            <div className="text-xs text-gray-800 mt-1">
                                                {!bQuickCollect && parseFloat(orderAmount) > 0 && (
                                                    <span>Base: {formatCurrency(parseFloat(orderAmount))}</span>
                                                )}
                                                {items.length > 0 && calculateGrandTotal() > 0 && (
                                                    <span>{(!bQuickCollect && parseFloat(orderAmount) > 0) ? ' + ' : ''}Items: {formatCurrency(calculateGrandTotal())}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-bold text-gray-900 tabular-nums">
                                                {formatCurrency(calculateOverallTotal())}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Cancel Order Checkbox - Only shown for pending orders */}
                        {(SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") && (
                            <div className="mt-4">
                                <div className="flex items-center">
                                    <Checkbox
                                        checked={cancelOrder}
                                        onChange={(e) => setCancelOrder(e.target.checked)}
                                        sx={{ color: 'red', '&.Mui-checked': { color: 'red' } }}
                                    />
                                    <span className="text-red-600 font-medium">Cancel Order?</span>
                                    <Tooltip
                                        title="The order will be cancelled and all the above selections will become invalid"
                                        placement="top"
                                        arrow
                                    >
                                        <IconButton size="small">
                                            <HelpOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </div>

                                {cancelOrder && isMonthlyOrMultiMonth && (
                                    <div className="flex items-center ml-2 mt-1">
                                        <Checkbox
                                            checked={pauseStudent}
                                            onChange={(e) => setPauseStudent(e.target.checked)}
                                            sx={{ color: '#2563eb', '&.Mui-checked': { color: '#2563eb' } }}
                                            size="small"
                                        />
                                        <span className="text-blue-700 text-sm font-medium">Do you want to pause the student?</span>
                                     
                                     
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notes Section - Only shown when cancel order is checked */}
                        {cancelOrder && (
                            <div className="mt-4">
                                <TextField
                                    value={orderNote}
                                    onChange={(e) => setOrderNote(e.target.value)}
                                    size="small"
                                    label="Cancellation Notes (Optional)"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="Add notes about why this order is being cancelled..."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        fontWeight: 400,
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}

                <div className={"links flex flex-col mt-4 gap-4"}>
                    <div className={"ml-2 flex gap-1 items-center cursor-pointer"} onClick={() => {
                        window.open(SelectedOrderDoc?.Invoice_pdf_link, '_blank')
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            className="feather feather-link-2 shrink-0">
                            <path
                                d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3"></path>
                            <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                        <p className={"text-cc_link underline underline-offset-2"}>Invoice Link</p>
                        {SelectedOrderDoc?.Invoice_pdf_link && (
                            <Tooltip title="Copy invoice link">
                                <IconButton
                                    size="small"
                                    className="!p-1 text-gray-600 hover:text-gray-900"
                                    aria-label="Copy invoice link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const url = SelectedOrderDoc.Invoice_pdf_link;
                                        navigator.clipboard.writeText(url).then(
                                            () => toast.success('Invoice link copied to clipboard'),
                                            () => toast.error('Failed to copy link'),
                                        );
                                    }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>

                    {SelectedOrderDoc?.receipt_link &&
                        <div className={"ml-2 flex gap-1 items-center cursor-pointer"} onClick={() => {
                            window.open(SelectedOrderDoc?.receipt_link, '_blank')
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none"
                                stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="feather feather-link-2 shrink-0">
                                <path
                                    d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3"></path>
                                <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            <p className={"text-cc_link underline underline-offset-2"}>Receipt Link</p>
                            <Tooltip title="Copy receipt link">
                                <IconButton
                                    size="small"
                                    className="!p-1 text-gray-600 hover:text-gray-900"
                                    aria-label="Copy receipt link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const url = SelectedOrderDoc.receipt_link;
                                        navigator.clipboard.writeText(url).then(
                                            () => toast.success('Receipt link copied to clipboard'),
                                            () => toast.error('Failed to copy link'),
                                        );
                                    }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </div>}


                </div>

                {/* Show different buttons based on order status */}
                {SelectedOrderDoc?.orderType === "writeoff" ? (
                    <div className={"flex justify-center"}>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleClose()
                            }}
                            className={`w-full bg-cc_brand mt-8 max-w-[200px] text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center `}>Okay
                        </button>
                    </div>
                ) : (
                    (SelectedOrderDoc?.paymentstatus === "Pending" || SelectedOrderDoc?.paymentstatus === "Failed") ?
                        <div className={"flex gap-2 pt-8"}>
                            <button disabled={isLoading} onClick={(e) => {
                                e.preventDefault();
                                handleClose()
                            }}
                                className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : ""} `}>Cancel
                            </button>
                            <button disabled={isLoading}
                                onClick={onSubmit}
                                className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : ""} `}>{isLoading &&
                                    <CircularProgress sx={{ color: "white" }} size={20} />}
                                <p className={"ml-2"}>{isLoading ? "Loading" : "Confirm"}</p>
                            </button>

                        </div> : <div className={"flex justify-center"}>
                            <button
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (orderNote.trim()) {
                                        Dispatch(toggleLoading(true));
                                        try {
                                            await update(SelectedOrderDoc?.id, {
                                                orderNote: orderNote.trim()
                                            });
                                            toast.success("Order note updated successfully");
                                        } catch (error) {
                                            toast.error("Failed to update order note");
                                        } finally {
                                            Dispatch(toggleLoading(false));
                                        }
                                    }
                                    handleClose();
                                }}
                                disabled={isLoading}
                                className={`w-full bg-cc_brand mt-8 max-w-[200px] text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : ""}`}>
                                {isLoading ? (
                                    <>
                                        <CircularProgress sx={{ color: "white" }} size={20} />
                                        <p className={"ml-2"}>Saving</p>
                                    </>
                                ) : "Okay"}
                            </button>
                        </div>
                )}

            </DialogContent>

        </Dialog>

    </React.Fragment>);
}